import api from './api';
import { 
  FinancialInstitutionResponse, 
  CreateFinancialInstitutionRequest,
  UpdateFinancialInstitutionRequest,
  FinancialInstitutionFilters 
} from '../types/financialInstitution';
import { ApiResponse } from '../types/api';
import { sanitizeFormData, detectSqlInjection, toISO8601Date, sanitizeUrl } from '../utils/sanitize';

/**
 * ✨ Convertit les valeurs d'enum en format backend (UPPER_CASE)
 * Le backend PostgreSQL utilise des enums en MAJUSCULES avec underscores
 */
const toBackendEnumValue = (value: string | undefined | null): string | undefined => {
  if (!value || value === '') return undefined;
  // Convertir en UPPER_CASE (siege_social → SIEGE_SOCIAL)
  return value.toUpperCase();
};

/**
 * Transforme les données d'institution financière pour le backend
 * - Convertit les dates en ISO 8601
 * - Supprime les champs vides (les convertit en undefined pour ne pas les envoyer)
 * - Assure que les strings obligatoires ne sont pas null
 * - Convertit les enums en format backend (UPPER_CASE)
 */
const transformInstitutionDataForBackend = <T extends Record<string, any>>(data: T): T => {
  const transformed = { ...data };

  console.log('🔍 Données avant transformation:', JSON.stringify(data, null, 2));

  // Liste des champs de date à transformer en ISO 8601
  // Ces champs sont REQUIS par le backend - on doit envoyer une date valide
  const dateFields = ['dateCreation', 'dateAgrement', 'validiteAgrement', 'dateDebutActivites'];
  
  for (const field of dateFields) {
    if (field in transformed) {
      const value = transformed[field];
      console.log(`📅 Champ date "${field}":`, value, typeof value);
      
      if (value && value !== '') {
        const isoDate = toISO8601Date(value);
        console.log(`📅 Conversion de "${field}":`, value, '→', isoDate);
        
        if (isoDate) {
          transformed[field] = isoDate;
        } else {
          // Si la conversion échoue, supprimer le champ (le backend décidera si c'est requis)
          console.warn(`⚠️ Conversion échouée pour "${field}", suppression du champ`);
          delete transformed[field];
        }
      } else {
        // Si vide, supprimer le champ - le backend validera si c'est requis
        delete transformed[field];
      }
    }
  }

  // Liste des champs string REQUIS par le backend
  // Si vides, on les garde comme strings vides "" au lieu de les supprimer
  const requiredStringFields = ['numeroAgrement', 'numeroRCCM', 'numeroNIF', 'sigle'];
  
  for (const field of requiredStringFields) {
    if (field in transformed) {
      const value = transformed[field];
      console.log(`📝 Champ string requis "${field}":`, value, typeof value);
      
      // Toujours convertir en string (même vide)
      if (value === null || value === undefined) {
        transformed[field] = '';
      } else if (typeof value !== 'string') {
        transformed[field] = String(value);
      }
      // Si c'est déjà une string (même vide ''), on la garde telle quelle
    } else {
      // Si le champ n'existe pas, l'ajouter comme string vide
      (transformed as any)[field] = '';
    }
  }

  // ✨ IMPORTANT: userId est OBLIGATOIRE et ne doit JAMAIS être vide
  // Le backend rejette les valeurs null ou string vide pour userId
  const userIdValue = transformed['userId'];
  console.log(`👤 userId dans transformation:`, userIdValue, typeof userIdValue);
  
  if (!userIdValue || userIdValue === '') {
    console.error('❌ userId manquant ou vide dans les données de transformation');
    console.error('❌ Données reçues:', JSON.stringify(data, null, 2));
    throw new Error('userId est obligatoire pour créer une institution financière. Veuillez rafraîchir la page.');
  }
  
  // S'assurer que c'est une string
  if (typeof userIdValue !== 'string') {
    transformed['userId'] = String(userIdValue);
  }

  // Liste des champs string OPTIONNELS - si vides, on les supprime
  const optionalStringFields = ['sousCategorie', 'codeSuccursale', 'institutionMereId'];
  
  for (const field of optionalStringFields) {
    if (field in transformed) {
      const value = transformed[field];
      console.log(`📝 Champ string optionnel "${field}":`, value, typeof value);
      
      if (value === '' || value === null || value === undefined) {
        delete transformed[field];
      } else if (typeof value !== 'string') {
        transformed[field] = String(value);
      }
    }
  }

  // ✨ ÉTAPE IMPORTANTE: Convertir les noms de champs frontend → backend
  // Le frontend utilise des accents (autoritéSupervision), le backend non (autoriteSupervision)
  const fieldMappings: Record<string, string> = {
    'autoritéSupervision': 'autoriteSupervision',
    // Ajouter d'autres mappings si nécessaire
  };
  
  for (const [frontendField, backendField] of Object.entries(fieldMappings)) {
    if (frontendField in transformed) {
      (transformed as any)[backendField] = transformed[frontendField];
      delete transformed[frontendField];
      console.log(`🔄 Renommage pour backend: ${frontendField} → ${backendField}`);
    }
  }

  // Liste des champs ENUM - si vides, on les supprime (le backend n'accepte que des valeurs valides)
  // ✨ IMPORTANT: Les valeurs restent en minuscules selon les enums backend
  // ✨ NOTE: Utiliser les noms backend (sans accents) car le mapping a été fait ci-dessus
  const enumFields = [
    'typeStructure',              // filiale, mere, independante, siege_social
    'statutJuridique',            // sa, sarl, cooperative, mutual, foundation, branch, other
    'autoriteSupervision',        // bcc, arca, asmf, other (nom backend, sans accent)
    'segmentClientelePrincipal',  // individuals, sme, large_enterprises, institutions, all_segments
    'motivationPrincipale',       // new_clients, portfolio_diversification, etc.
  ];
  
  for (const field of enumFields) {
    if (field in transformed) {
      const value = transformed[field];
      console.log(`🔤 Champ enum "${field}":`, value, typeof value);
      
      if (value === '' || value === null || value === undefined) {
        console.log(`🗑️ Suppression du champ enum vide "${field}"`);
        delete transformed[field];
      }
      // Les valeurs restent telles quelles (minuscules)
    }
  }

  // Supprimer les autres champs vides/undefined (sauf ceux requis traités ci-dessus)
  // ✨ IMPORTANT: Ne JAMAIS supprimer userId - il est obligatoire pour la création
  // ✨ ANTI-PERTE DE DONNÉES: Ne pas supprimer les tableaux vides - ils représentent une intention explicite
  const protectedFields = [...requiredStringFields, 'userId'];
  
  // ✨ VALIDER LES CHAMPS URL (le backend attend des URLs valides avec @IsUrl())
  // Ces champs doivent contenir des URLs Cloudinary ou autres URLs valides
  const urlFields = ['logo', 'ceoPhoto', 'siteWeb'];
  for (const field of urlFields) {
    if (field in transformed && transformed[field]) {
      const validUrl = sanitizeUrl(transformed[field]);
      if (validUrl) {
        transformed[field] = validUrl;
      } else {
        // URL invalide → supprimer le champ (optionnel dans le DTO)
        console.log(`🔗 URL invalide pour "${field}", suppression du champ`);
        delete transformed[field];
      }
    }
  }
  
  for (const key in transformed) {
    // Ne pas supprimer les champs protégés (requis + userId)
    if (protectedFields.includes(key)) continue;
    
    const value = transformed[key];
    
    // ✨ Conserver les tableaux (même vides) - ce sont des données valides
    if (Array.isArray(value)) {
      continue;
    }
    
    if (value === undefined) {
      delete transformed[key];
    }
    // Garder null seulement si explicitement voulu
    if (value === null && !['institutionMere'].includes(key)) {
      delete transformed[key];
    }
  }

  console.log('✅ Données après transformation:', JSON.stringify(transformed, null, 2));

  return transformed;
};

/**
 * Sanitise et transforme les données d'une institution financière avant envoi
 * - Détecte les injections SQL
 * - Sanitise les champs texte
 * - Transforme les dates en ISO 8601
 * - Supprime les champs vides pour éviter les erreurs de validation
 */
const sanitizeInstitutionData = <T extends Record<string, any>>(data: T): T => {
  console.log('🔍 Données brutes reçues:', JSON.stringify(data, null, 2));

  // ✨ ÉTAPE 1: Transformation et nettoyage AVANT sanitisation
  // Ceci permet de supprimer les champs vides et transformer les dates
  // avant que sanitizeFormData ne puisse les modifier
  const transformed = transformInstitutionDataForBackend(data);
  
  console.log('🔄 Données après transformation:', JSON.stringify(transformed, null, 2));

  // ✨ ÉTAPE 2: Vérification des tentatives d'injection SQL
  const jsonString = JSON.stringify(transformed);
  if (detectSqlInjection(jsonString)) {
    console.warn('⚠️ Tentative d\'injection SQL détectée dans les données institution');
    throw new Error('Données invalides détectées. Veuillez vérifier vos entrées.');
  }

  // ✨ ÉTAPE 3: Sanitisation des champs texte (sur les données déjà nettoyées)
  const sanitized = sanitizeFormData(transformed, {
    denominationSociale: { maxLength: 200 },
    sigle: { maxLength: 50 },
    description: { maxLength: 5000 },
    email: { toLowerCase: true },
    numeroAgrement: { maxLength: 100 },
    numeroRCCM: { maxLength: 100 },
    numeroNIF: { maxLength: 100 },
  } as any);
  
  console.log('🔧 Données finales pour le backend:', JSON.stringify(sanitized, null, 2));

  return sanitized;
};

/**
 * ✨ Transforme les données du backend vers le format frontend
 * Le backend utilise des noms sans accents, le frontend avec accents
 * Mapping: autoriteSupervision → autoritéSupervision
 */
const transformBackendToFrontend = <T extends Record<string, any>>(data: T): T => {
  if (!data) return data;
  
  const transformed = { ...data };
  
  // Mapping des champs backend (sans accents) vers frontend (avec accents)
  const fieldMappings: Record<string, string> = {
    'autoriteSupervision': 'autoritéSupervision',
    // Ajouter d'autres mappings si nécessaire
  };
  
  for (const [backendField, frontendField] of Object.entries(fieldMappings)) {
    if (backendField in transformed && !(frontendField in transformed)) {
      (transformed as any)[frontendField] = transformed[backendField];
      delete transformed[backendField];
      console.log(`🔄 Mapping backend→frontend: ${backendField} → ${frontendField}`);
    }
  }
  
  return transformed;
};

/**
 * ✨ Transforme les données du frontend vers le format backend
 * Le frontend utilise des noms avec accents, le backend sans accents
 * Mapping: autoritéSupervision → autoriteSupervision
 */
const transformFrontendToBackend = <T extends Record<string, any>>(data: T): T => {
  if (!data) return data;
  
  const transformed = { ...data };
  
  // Mapping inverse des champs frontend (avec accents) vers backend (sans accents)
  const fieldMappings: Record<string, string> = {
    'autoritéSupervision': 'autoriteSupervision',
    // Ajouter d'autres mappings si nécessaire
  };
  
  for (const [frontendField, backendField] of Object.entries(fieldMappings)) {
    if (frontendField in transformed && !(backendField in transformed)) {
      (transformed as any)[backendField] = transformed[frontendField];
      delete transformed[frontendField];
      console.log(`🔄 Mapping frontend→backend: ${frontendField} → ${backendField}`);
    }
  }
  
  return transformed;
};

/**
 * Service pour la gestion des institutions financières
 * ✨ SIMPLIFIÉ: Transmet TOUTES les données sans transformation
 * 🔒 SÉCURISÉ: Sanitisation des entrées contre les injections
 * Les formulaires sont la source de vérité
 */
class FinancialInstitutionApiService {
  private baseUrl = '/financial-institutions';

  /**
   * Crée une nouvelle institution financière
   * @param data - TOUTES les données du formulaire FinancialInstitutionForm
   */
  async createInstitution(data: CreateFinancialInstitutionRequest): Promise<FinancialInstitutionResponse> {
    // ✨ Log explicite du userId AVANT sanitisation
    console.log('👤 userId reçu dans createInstitution:', data.userId);
    
    const sanitizedData = sanitizeInstitutionData(data);
    
    // ✨ Vérification que userId est toujours présent après sanitisation
    console.log('👤 userId après sanitisation:', (sanitizedData as any).userId);
    
    if (!(sanitizedData as any).userId) {
      console.error('❌ ERREUR: userId manquant après sanitisation!');
      throw new Error('userId est obligatoire pour créer une institution financière');
    }
    
    console.log('📤 FinancialInstitutionApiService.createInstitution - Envoi de TOUTES les données:', sanitizedData);
    const response = await api.post<ApiResponse<FinancialInstitutionResponse>>(this.baseUrl, sanitizedData);
    console.log('✅ FinancialInstitutionApiService.createInstitution - Réponse reçue:', response.data);
    return response.data;
  }

  /**
   * Récupère une institution financière par son ID
   * ✨ Transforme les données backend vers format frontend (mapping des champs)
   */
  async getInstitution(institutionId: string): Promise<FinancialInstitutionResponse> {
    console.log(`📥 FinancialInstitutionApiService.getInstitution - Récupération de l'institution ${institutionId}`);
    const response = await api.get<ApiResponse<FinancialInstitutionResponse>>(`${this.baseUrl}/${institutionId}`);
    
    // 🔍 Debug: Afficher la structure de la réponse
    console.log('📦 Réponse brute du backend:', response);
    console.log('📦 response.data:', (response as any).data);
    console.log('📦 response.id:', (response as any).id);
    
    // ✨ Gérer les deux formats possibles de réponse:
    // 1. { success: true, data: {...institution...} } → extraire response.data
    // 2. {...institution...} directement → utiliser response
    const institutionData = (response as any).data && (response as any).data.id 
      ? (response as any).data 
      : response;
    
    console.log('📦 Données institution extraites:', institutionData);
    
    // ✨ Transformer les données backend → frontend (ex: autoriteSupervision → autoritéSupervision)
    const transformedData = transformBackendToFrontend(institutionData);
    console.log('✅ Données transformées pour le frontend:', transformedData);
    
    return transformedData;
  }

  /**
   * Met à jour une institution financière
   * @param data - TOUTES les données modifiées (partielles ou complètes)
   */
  async updateInstitution(institutionId: string, data: UpdateFinancialInstitutionRequest): Promise<FinancialInstitutionResponse> {
    const sanitizedData = sanitizeInstitutionData(data);
    console.log(`📤 FinancialInstitutionApiService.updateInstitution - Envoi de TOUTES les données pour ${institutionId}:`, sanitizedData);
    const response = await api.patch<ApiResponse<FinancialInstitutionResponse>>(`${this.baseUrl}/${institutionId}`, sanitizedData);
    console.log('✅ FinancialInstitutionApiService.updateInstitution - Réponse reçue:', response.data);
    return response.data;
  }

  /**
   * @deprecated Utiliser cloudinaryService.uploadLogo() puis updateInstitution({ logo: url }) à la place.
   * Cette méthode envoie le fichier directement au backend, ce qui n'est pas recommandé.
   * Le flux correct: Fichier → Cloudinary → URL → Backend
   */
  async uploadLogo(institutionId: string, logoFile: File): Promise<{ url: string }> {
    console.warn('⚠️ uploadLogo est déprécié. Utilisez cloudinaryService.uploadLogo() puis updateInstitution()');
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${institutionId}/logo`,
      logoFile,
      'logo'
    );
    return response.data;
  }

  /**
   * @deprecated Utiliser cloudinaryService.uploadAvatar() puis updateInstitution({ ceo: { ...ceo, photo: url }}) à la place.
   * Cette méthode envoie le fichier directement au backend, ce qui n'est pas recommandé.
   * Le flux correct: Fichier → Cloudinary → URL → Backend
   */
  async uploadCeoPhoto(institutionId: string, photoFile: File): Promise<{ url: string }> {
    console.warn('⚠️ uploadCeoPhoto est déprécié. Utilisez cloudinaryService.uploadAvatar() puis updateInstitution()');
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${institutionId}/ceo-photo`,
      photoFile,
      'photo'
    );
    return response.data;
  }

  async addBranch(institutionId: string, branchData: {
    name: string;
    address: string;
    phone: string;
    manager?: string;
  }): Promise<{ id: string; message: string }> {
    const response = await api.post<ApiResponse<{ id: string; message: string }>>(
      `${this.baseUrl}/${institutionId}/branches`,
      branchData
    );
    return response.data;
  }

  async removeBranch(institutionId: string, branchId: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${institutionId}/branches/${branchId}`
    );
    return response.data;
  }

  async addTeamMember(institutionId: string, memberData: {
    name: string;
    position: string;
    bio?: string;
  }): Promise<{ id: string; message: string }> {
    const response = await api.post<ApiResponse<{ id: string; message: string }>>(
      `${this.baseUrl}/${institutionId}/team`,
      memberData
    );
    return response.data;
  }

  async removeTeamMember(institutionId: string, memberId: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${institutionId}/team/${memberId}`
    );
    return response.data;
  }

  /**
   * @deprecated Utiliser cloudinaryService.uploadAvatar() puis utiliser l'URL lors de l'ajout du membre.
   * Cette méthode envoie le fichier directement au backend, ce qui n'est pas recommandé.
   * Le flux correct: Fichier → Cloudinary → URL → Backend
   */
  async uploadTeamMemberPhoto(institutionId: string, memberId: string, photoFile: File): Promise<{ url: string }> {
    console.warn('⚠️ uploadTeamMemberPhoto est déprécié. Utilisez cloudinaryService.uploadAvatar() à la place');
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${institutionId}/team/${memberId}/photo`,
      photoFile,
      'photo'
    );
    return response.data;
  }

  async listInstitutions(filters?: FinancialInstitutionFilters): Promise<{
    institutions: FinancialInstitutionResponse[];
    meta: { pagination: { page: number; limit: number; total: number; pages: number } };
  }> {
    const response = await api.get<ApiResponse<FinancialInstitutionResponse[]>>(this.baseUrl, filters);
    return {
      institutions: response.data,
      meta: response.meta?.pagination ? { pagination: response.meta.pagination } : { pagination: { page: 1, limit: 10, total: 0, pages: 0 } }
    };
  }

  /**
   * ✨ Récupère l'institution financière de l'utilisateur connecté
   * Utilise la liste filtrée par userId pour trouver l'institution
   * @param userId - L'ID de l'utilisateur connecté
   * @returns L'institution de l'utilisateur ou null si non trouvée
   */
  async getMyInstitution(userId: string): Promise<FinancialInstitutionResponse | null> {
    if (!userId) {
      console.warn('⚠️ getMyInstitution: userId manquant');
      return null;
    }
    
    console.log(`📥 FinancialInstitutionApiService.getMyInstitution - Recherche d'institution pour userId: ${userId}`);
    
    try {
      // Utiliser le endpoint de liste avec le userId comme filtre implicite
      // Le backend devrait retourner les institutions de l'utilisateur
      const response = await api.get<ApiResponse<FinancialInstitutionResponse[]>>(this.baseUrl);
      
      // 🔍 Debug: Afficher la structure complète de la réponse
      console.log('📦 Réponse brute getMyInstitution (complète):', JSON.stringify(response, null, 2));
      console.log('📦 response.data:', response.data);
      console.log('📦 Type de response.data:', typeof response.data, Array.isArray(response.data));
      
      // Le backend retourne { data: [...], meta: {...} }
      // response est déjà l'objet { data: [...], meta: {...} }
      const institutions = response.data || [];
      
      console.log('📦 Nombre d\'institutions trouvées:', institutions.length);
      console.log('📦 UserIds des institutions:', institutions.map((inst: any) => inst.userId));
      console.log('📦 UserId recherché:', userId);
      
      // Filtrer pour trouver l'institution de cet utilisateur
      const userInstitution = institutions.find((inst: any) => inst.userId === userId);
      
      if (userInstitution) {
        console.log('✅ Institution trouvée pour l\'utilisateur:', userInstitution.denominationSociale);
        console.log('📦 Données brutes de l\'institution:', JSON.stringify(userInstitution, null, 2));
        // Transformer les données backend → frontend
        const transformed = transformBackendToFrontend(userInstitution);
        console.log('📦 Données transformées:', JSON.stringify(transformed, null, 2));
        return transformed;
      }
      
      console.log('ℹ️ Aucune institution trouvée pour cet utilisateur');
      console.log('ℹ️ Comparaison userId:', { searchedUserId: userId, availableUserIds: institutions.map((inst: any) => inst.userId) });
      return null;
    } catch (error) {
      console.error('❌ Erreur lors de la recherche de l\'institution utilisateur:', error);
      return null;
    }
  }
}

export const financialInstitutionApiService = new FinancialInstitutionApiService();
export default financialInstitutionApiService;
